import React, { memo } from 'react';
import { FiUserPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { CardChangeTdaVip } from './CardChangeTdaVip';
import { CardPosition } from './CardPosition';
import { ChatBox, userTextChat } from './ChatBox';
import { Statistics } from './Statistics';

import icPodium from '@/assets/ic_podium.svg';
import { Button, Card, Divider } from '@/components';
import { TabType } from '@/components/Types';
import { useIsLastMatchDay } from '@/hooks/useIsLastMatchDate';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import { TopRankingTDA, ViewTDAResponse } from '@/types/tda';
import { cn } from '@/utils';

type Props = {
  tab: TabType;
  tourney: ViewTDAResponse | null;
  onClickChangeToVip: () => void;
  onClickOpenVideoModal: () => void;
};

const getUsersFromTourney = (tourneyData: any) => {
  const topGeneralUsers: userTextChat[] =
    tourneyData.tourney?.top_general?.map((element: TopRankingTDA) => ({
      id: element.idUsuarioDT.toString(),
      display: element.dt,
    })) ?? [];

  const topFechaUsers: userTextChat[] =
    tourneyData.tourney?.top_fecha?.map((element: TopRankingTDA) => ({
      id: element.idUsuarioDT.toString(),
      display: element.dt,
    })) ?? [];

  const combinedUsers = [...topGeneralUsers, ...topFechaUsers];
  const uniqueUsers = Array.from(
    new Map(combinedUsers.map((user) => [user.id, user])).values()
  );

  return uniqueUsers;
};

export const SidebarContentView: React.FC<Props> = memo(
  ({ tab, tourney, onClickChangeToVip, onClickOpenVideoModal }) => {
    const { tourneyData } = useTourney();
    const navigate = useNavigate();

    const combinedUsers = getUsersFromTourney(tourneyData);
    const isLastMatchDay = useIsLastMatchDay();

    return (
      <div className="lg:w-[426px]">
        <div className="max-h-[214px] h-screen">
          {tourneyData.isOwner && (
            <div
              className={cn(
                'flex flex-col justify-center items-center w-full h-full'
              )}
            >
              <Button
                className="w-[332px] h-[50px]"
                disabled={isLastMatchDay}
                type="button"
                variant="primary"
                onClick={() => navigate(ROUTES.adminTourney)}
              >
                Administrar
              </Button>
              <Button
                className="mt-3 w-[332px] h-[50px]"
                disabled={isLastMatchDay}
                type="button"
                variant="secondary"
                onClick={() => navigate(ROUTES.InviteToFriendsTournament)}
              >
                Invitar amigos
              </Button>
              {tourneyData.pendingInvitations !== 0 && (
                <Button
                  className="mt-5 flex items-center space-x-3 underline-offset-4 font-semibold "
                  disabled={isLastMatchDay}
                  type="button"
                  variant="text"
                  onClick={() => navigate(ROUTES.adminTourney)}
                >
                  <figure className="rounded-full bg-gdt-blue-light2 w-7 h-7 flex justify-center items-center">
                    <FiUserPlus />
                  </figure>
                  <p>
                    Tenés {tourneyData.pendingInvitations}{' '}
                    {tourneyData.pendingInvitations !== 1
                      ? 'solicitudes'
                      : 'solicitud'}
                  </p>
                </Button>
              )}
            </div>
          )}
        </div>
        <Card className={cn('p-0 bg-white w-full px-5 pt-6 pb-3', {})}>
          <div className="flex items-center space-x-2">
            <figure>
              <img alt="Icono de titulo Mi Desempeño" src={icPodium} />
            </figure>
            <p className="font-bold text-lg">Mi desempeño</p>
          </div>
          {/* card posicion en ... */}
          <CardPosition tab={tab} tourney={tourney} />

          {/* divisor */}
          {!tourneyData.isVip && tourneyData.isOwner && (
            <>
              <Divider
                className="bg-gdt-gray-6 w-[calc(100%+4rem)] relative -left-[2rem] mt-6"
                type="horizontal"
              />

              {/* card cambiar a vip */}
              <CardChangeTdaVip
                onClickChangeToVip={onClickChangeToVip}
                onClickOpenVideoModal={onClickOpenVideoModal}
              />
            </>
          )}
        </Card>

        {/* Se deja comentado momentaneamente por DTA-6794
        {tourneyData.isVip && tourneyData.isOwner && (
          <Card className="p-0 bg-white mt-1 w-full flex items-center justify-between h-[92px] px-6">
            <p className="font-semibold">Cargá una imagen de fondo</p>
            <Button
              className="w-36"
              type="button"
              variant="secondary"
              onClick={() => {
                handleSetTourney({
                  ...tourneyData,
                  showModalCover: true,
                });
              }}
            >
              Editar
            </Button>
          </Card>
        )}
        */}

        <Card className="p-0 bg-white mt-3 w-full rounded-[8px]">
          <ChatBox users={combinedUsers} />
        </Card>

        {tourneyData.isVip && <Statistics.Records data={tourneyData.records} />}
      </div>
    );
  }
);

SidebarContentView.displayName = 'SidebarContentView';
